import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  AccountBalanceWalletOutlined as WalletIcon,
  ArrowDownward as CreditIcon,
  ArrowUpward as DebitIcon,
  ReceiptLongOutlined as TransactionIcon,
  Verified as VerifiedIcon,
  OpenInNew as OpenIcon,
} from "@mui/icons-material";
import { FaDollarSign } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ProfileSideBar from "./ProfileSideBar";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import config from "../../Config";

const COLORS = {
  cream: "#f7f4ef",
  softCream: "#fbf9f6",
  black: "#171717",
  mutedBlack: "#3a3a3a",
  grey: "#77736d",
  lightGrey: "#a29d95",
  border: "#ddd7ce",
  gold: "#977743",
  goldLight: "#c4a36a",
  green: "#55745c",
  red: "#8b5e55",
};

const formatAmount = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "₹0";
  }

  return `₹${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const WalletCard = ({ title, amount, description, icon, dark = false }) => {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 0,
        border: dark
          ? `1px solid ${COLORS.black}`
          : `1px solid ${COLORS.border}`,
        backgroundColor: dark ? COLORS.black : COLORS.softCream,
        color: dark ? "#fff" : COLORS.black,
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2.5, sm: 3 },
          "&:last-child": {
            pb: { xs: 2.5, sm: 3 },
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 11,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: dark ? "#bdb8b0" : COLORS.grey,
                mb: 1.5,
                fontWeight: 600,
              }}
            >
              {title}
            </Typography>

            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: {
                  xs: 28,
                  sm: 34,
                },
                lineHeight: 1.1,
                fontWeight: 400,
                overflowWrap: "anywhere",
              }}
            >
              {amount}
            </Typography>
          </Box>

          <Box
            sx={{
              width: 44,
              height: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: dark ? "1px solid #3d3d3d" : `1px solid ${COLORS.border}`,
              color: dark ? COLORS.goldLight : COLORS.gold,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        </Stack>

        <Typography
          sx={{
            mt: 2.5,
            fontSize: 13,
            lineHeight: 1.6,
            color: dark ? "#aaa59e" : COLORS.grey,
          }}
        >
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

const TransactionRow = ({ transaction, onOrderClick }) => {
  const isCredit =
    String(transaction?.transactionType || "").toLowerCase() === "credit";

  const transactionType = String(
    transaction?.transactionType || "debit",
  ).toLowerCase();

  const transactionStatus = String(
    transaction?.transactionStatus || "",
  ).toLowerCase();

  const amount = Number(transaction?.amount) || 0;
  const fee = Number(transaction?.fee) || 0;

  return (
    <Box
      sx={{
        px: {
          xs: 2,
          sm: 3.5,
        },
        py: {
          xs: 2.5,
          sm: 3,
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: COLORS.cream,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `1px solid ${COLORS.border}`,
            color: isCredit ? COLORS.green : COLORS.red,
          }}
        >
          {isCredit ? (
            <CreditIcon fontSize="small" />
          ) : (
            <DebitIcon fontSize="small" />
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            justifyContent="space-between"
            alignItems={{
              xs: "flex-start",
              sm: "flex-start",
            }}
            gap={2}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: COLORS.black,
                  mb: 0.6,
                }}
              >
                {isCredit ? "Wallet Credit" : "Wallet Payment"}
              </Typography>

              <Typography
                sx={{
                  fontSize: 12,
                  color: COLORS.grey,
                  mb: 1.5,
                }}
              >
                {formatDateTime(transaction?.createdAt)}
              </Typography>

              <Stack direction="row" flexWrap="wrap" gap={1}>
                {transaction?.transactionId && (
                  <Box
                    sx={{
                      px: 1.2,
                      py: 0.6,
                      border: `1px solid ${COLORS.border}`,
                      backgroundColor: "#fff",
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 10,
                        color: COLORS.lightGrey,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        mr: 0.7,
                      }}
                    >
                      Transaction
                    </Typography>

                    <Typography
                      component="span"
                      sx={{
                        fontSize: 11,
                        color: COLORS.mutedBlack,
                        fontWeight: 600,
                      }}
                    >
                      {transaction.transactionId}
                    </Typography>
                  </Box>
                )}

                {transaction?.orderId && (
                  <Box
                    onClick={() => onOrderClick(transaction.orderId)}
                    sx={{
                      px: 1.2,
                      py: 0.6,
                      border: `1px solid ${COLORS.border}`,
                      backgroundColor: "#fff",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.6,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: COLORS.gold,
                        backgroundColor: "#f8f3e9",
                      },
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 10,
                        color: COLORS.lightGrey,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        mr: 0.2,
                      }}
                    >
                      Order
                    </Typography>

                    <Typography
                      component="span"
                      sx={{
                        fontSize: 11,
                        color: COLORS.gold,
                        fontWeight: 700,
                        textDecoration: "underline",
                        textUnderlineOffset: "3px",
                      }}
                    >
                      {transaction.orderId}
                    </Typography>

                    <OpenIcon
                      sx={{
                        fontSize: 13,
                        color: COLORS.gold,
                      }}
                    />
                  </Box>
                )}
              </Stack>
            </Box>

            <Box
              sx={{
                textAlign: {
                  xs: "left",
                  sm: "right",
                },
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: {
                    xs: 16,
                    sm: 18,
                  },
                  fontWeight: 700,
                  color: isCredit ? COLORS.green : COLORS.red,
                  whiteSpace: "nowrap",
                }}
              >
                {isCredit ? "+" : "-"}
                {formatAmount(Math.abs(amount))}
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: COLORS.lightGrey,
                }}
              >
                {transactionType}
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              mt: 2,
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr 1fr",
                sm: "repeat(3, 1fr)",
              },
              gap: 1,
            }}
          >
            <Box
              sx={{
                borderTop: `1px solid ${COLORS.border}`,
                pt: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 9,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: COLORS.lightGrey,
                  mb: 0.4,
                }}
              >
                Status
              </Typography>

              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color:
                    transactionStatus === "refunded"
                      ? COLORS.gold
                      : COLORS.mutedBlack,
                  textTransform: "capitalize",
                }}
              >
                {transaction?.transactionStatus || "—"}
              </Typography>
            </Box>

            <Box
              sx={{
                borderTop: `1px solid ${COLORS.border}`,
                pt: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 9,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: COLORS.lightGrey,
                  mb: 0.4,
                }}
              >
                Fee
              </Typography>

              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: COLORS.mutedBlack,
                }}
              >
                {formatAmount(fee)}
              </Typography>
            </Box>

            <Box
              sx={{
                borderTop: `1px solid ${COLORS.border}`,
                pt: 1,
                gridColumn: {
                  xs: "1 / -1",
                  sm: "auto",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 9,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: COLORS.lightGrey,
                  mb: 0.4,
                }}
              >
                Reference
              </Typography>

              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: COLORS.mutedBlack,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={transaction?.referenceId || ""}
              >
                {transaction?.referenceId || "—"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const Earnings = () => {
  const navigate = useNavigate();

  const [wallet, setWallet] = useState({
    amount: 0,
    pendingAmount: 0,
    securityAmount: 0,
  });

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transactionLoading, setTransactionLoading] = useState(true);
  const [error, setError] = useState("");

  const userId =
    sessionStorage.getItem("user-id") || sessionStorage.getItem("userId");

  const fetchWallet = useCallback(async () => {
    if (!userId) {
      return;
    }

    try {
      const response = await fetch(`${config.apiBaseUrl}/${config.getWellet}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "user-id": userId,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Unable to load wallet.");
      }

      setWallet({
        amount: Number(data?.walletData?.amount) || 0,
        pendingAmount: Number(data?.walletData?.pendingAmount) || 0,
        securityAmount: Number(data?.walletData?.securityAmount) || 0,
      });
    } catch (err) {
      setError(err?.message || "Unable to load wallet details.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchTransactions = useCallback(async () => {
    if (!userId) {
      return;
    }

    try {
      const response = await fetch(
        `${config.apiBaseUrl}/${config.transactionHistory}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "user-id": userId,
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Unable to load transaction history.");
      }

      setTransactions(
        Array.isArray(data?.transactionHistory) ? data.transactionHistory : [],
      );
    } catch (err) {
      setError(err?.message || "Unable to load transaction history.");
      setTransactions([]);
    } finally {
      setTransactionLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setTransactionLoading(false);

      Swal.fire({
        icon: "warning",
        title: "Sign in required",
        text: "Please sign in to view your earnings.",
        confirmButtonText: "Sign In",
        confirmButtonColor: COLORS.black,
      }).then(() => {
        navigate("/Signin");
      });

      return;
    }

    fetchWallet();
    fetchTransactions();
  }, [userId, navigate, fetchWallet, fetchTransactions]);

  const totalWalletValue = useMemo(() => {
    return (
      Number(wallet.amount || 0) +
      Number(wallet.pendingAmount || 0) +
      Number(wallet.securityAmount || 0)
    );
  }, [wallet]);

  const sortedTransactions = useMemo(() => {
    return [...transactions].sort((a, b) => {
      const dateA = new Date(
        a?.createdAt || a?.updatedAt || a?.transactionDate || 0,
      ).getTime();

      const dateB = new Date(
        b?.createdAt || b?.updatedAt || b?.transactionDate || 0,
      ).getTime();

      return dateB - dateA;
    });
  }, [transactions]);

  const handleOrderClick = (orderId) => {
    if (!orderId) {
      return;
    }

    navigate(`/orderDetail/${orderId}`);
  };

  if (!userId) {
    return null;
  }

  return (
    <>
      <Navbar />

      <Box
        sx={{
          minHeight: "calc(100vh - 80px)",
          backgroundColor: COLORS.cream,
          color: COLORS.black,
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: 1440,
            px: {
              xs: 2,
              sm: 3,
              md: 5,
            },
            py: {
              xs: 4,
              sm: 5,
              md: 7,
            },
          }}
        >
          <Box
            sx={{
              mb: {
                xs: 4,
                md: 6,
              },
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: COLORS.gold,
                fontWeight: 700,
                mb: 1.5,
              }}
            >
              Your Earnings
            </Typography>

            <Typography
              component="h1"
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: {
                  xs: 34,
                  sm: 42,
                  md: 52,
                },
                lineHeight: 1.08,
                fontWeight: 400,
                mb: 1.5,
              }}
            >
              Your wallet, your rewards.
            </Typography>

            <Typography
              sx={{
                maxWidth: 650,
                fontSize: {
                  xs: 14,
                  md: 15,
                },
                lineHeight: 1.8,
                color: COLORS.grey,
              }}
            >
              Track your available earnings, pending amounts, security balance
              and complete transaction history in one place.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "250px minmax(0, 1fr)",
              },
              gap: {
                xs: 3,
                md: 5,
              },
              alignItems: "start",
            }}
          >
            <ProfileSideBar active="earning" />

            <Box sx={{ minWidth: 0 }}>
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 0,
                    border: `1px solid ${COLORS.border}`,
                    backgroundColor: "#fffaf7",
                  }}
                >
                  {error}
                </Alert>
              )}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 2,
                  mb: 4,
                }}
              >
                <WalletCard
                  title="Available Balance"
                  amount={
                    loading ? (
                      <CircularProgress
                        size={25}
                        sx={{
                          color: COLORS.gold,
                        }}
                      />
                    ) : (
                      formatAmount(wallet.amount)
                    )
                  }
                  description="Amount currently available in your wallet."
                  icon={<WalletIcon />}
                />

                <WalletCard
                  title="Pending Amount"
                  amount={
                    loading ? (
                      <CircularProgress
                        size={25}
                        sx={{
                          color: COLORS.gold,
                        }}
                      />
                    ) : (
                      formatAmount(wallet.pendingAmount)
                    )
                  }
                  description="Amount currently waiting for settlement."
                  icon={<TransactionIcon />}
                />

                <WalletCard
                  title="Security Amount"
                  amount={
                    loading ? (
                      <CircularProgress
                        size={25}
                        sx={{
                          color: COLORS.gold,
                        }}
                      />
                    ) : (
                      formatAmount(wallet.securityAmount)
                    )
                  }
                  description="Security amount maintained in your wallet."
                  icon={<VerifiedIcon />}
                />

                <WalletCard
                  title="Total Wallet Value"
                  amount={
                    loading ? (
                      <CircularProgress
                        size={25}
                        sx={{
                          color: COLORS.goldLight,
                        }}
                      />
                    ) : (
                      formatAmount(totalWalletValue)
                    )
                  }
                  description="Combined value of all wallet balances."
                  icon={<FaDollarSign />}
                  dark
                />
              </Box>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 0,
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.softCream,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: {
                      xs: 2.5,
                      sm: 3.5,
                    },
                    py: 3,
                    display: "flex",
                    alignItems: {
                      xs: "flex-start",
                      sm: "center",
                    },
                    justifyContent: "space-between",
                    flexDirection: {
                      xs: "column",
                      sm: "row",
                    },
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 11,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: COLORS.gold,
                        fontWeight: 700,
                        mb: 1,
                      }}
                    >
                      Wallet Activity
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: {
                          xs: 26,
                          sm: 30,
                        },
                        fontWeight: 400,
                      }}
                    >
                      Transaction History
                    </Typography>
                  </Box>

                  <Chip
                    label={`${transactions.length} ${
                      transactions.length === 1 ? "Transaction" : "Transactions"
                    }`}
                    sx={{
                      borderRadius: 0,
                      backgroundColor: "#f0ece5",
                      color: COLORS.mutedBlack,
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.06em",
                    }}
                  />
                </Box>

                <Divider
                  sx={{
                    borderColor: COLORS.border,
                  }}
                />

                {transactionLoading ? (
                  <Box
                    sx={{
                      minHeight: 260,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress
                      sx={{
                        color: COLORS.gold,
                      }}
                    />
                  </Box>
                ) : sortedTransactions.length === 0 ? (
                  <Box
                    sx={{
                      minHeight: 280,
                      px: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 58,
                        height: 58,
                        border: `1px solid ${COLORS.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: COLORS.gold,
                        mb: 2,
                      }}
                    >
                      <TransactionIcon />
                    </Box>

                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: 24,
                        mb: 1,
                      }}
                    >
                      No transactions yet
                    </Typography>

                    <Typography
                      sx={{
                        maxWidth: 450,
                        fontSize: 13,
                        lineHeight: 1.7,
                        color: COLORS.grey,
                      }}
                    >
                      Your wallet activity will appear here once you have
                      completed a transaction.
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {sortedTransactions.map((transaction, index) => (
                      <React.Fragment
                        key={
                          transaction?._id ||
                          transaction?.transactionId ||
                          index
                        }
                      >
                        <TransactionRow
                          transaction={transaction}
                          onOrderClick={handleOrderClick}
                        />

                        {index !== sortedTransactions.length - 1 && (
                          <Divider
                            sx={{
                              borderColor: COLORS.border,
                            }}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                )}
              </Paper>

              <Box
                sx={{
                  mt: 3,
                  p: {
                    xs: 2.5,
                    sm: 3,
                  },
                  backgroundColor: COLORS.black,
                  color: "#fff",
                  border: `1px solid ${COLORS.black}`,
                }}
              >
                <Stack
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                  alignItems={{
                    xs: "flex-start",
                    sm: "center",
                  }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "0.15em",
                        color: COLORS.goldLight,
                        fontWeight: 700,
                        mb: 1,
                      }}
                    >
                      Need assistance?
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: 21,
                        fontWeight: 400,
                        mb: 0.5,
                      }}
                    >
                      Questions about your wallet?
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 13,
                        color: "#aaa59e",
                        lineHeight: 1.6,
                      }}
                    >
                      Contact our support team for help with your earnings or
                      transactions.
                    </Typography>
                  </Box>

                  <Button
                    onClick={() => navigate("/contact-us")}
                    variant="outlined"
                    sx={{
                      minWidth: 150,
                      minHeight: 44,
                      borderRadius: 0,
                      borderColor: "#77736d",
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      "&:hover": {
                        borderColor: COLORS.goldLight,
                        backgroundColor: "rgba(196,163,106,0.08)",
                      },
                    }}
                  >
                    Contact Support
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer />
    </>
  );
};

export default Earnings;
